import React from "react";

/**
 * Two tab strips for the GRC pages.
 * - ChromeTabs: browser-style tabs whose active tab joins the panel under it (put the panel straight
 *   after the strip, with no top border: TAB_PANEL_CLASS).
 * - SegmentedTabs: a switch inside a page section, the active choice a raised pill.
 * Items: { key, label, icon?: lucide component, badge?: number, disabled?, note?, testId? }.
 */

// Chrome's inward curve at the foot of the active tab, on one side.
const Curve = ({ side }) => (
  <span aria-hidden="true" className={`absolute bottom-0 w-3 h-3 pointer-events-none ${side === "left" ? "-left-3" : "-right-3"}`}
    style={{ background: `radial-gradient(circle at ${side === "left" ? "0 0" : "100% 0"}, transparent 11.5px, #fff 12px)` }} />
);

export const TAB_PANEL_CLASS = "bg-white border border-t-0 border-gray-200 rounded-b-xl shadow-sm p-4 space-y-4";

export function ChromeTabs({ items, value, onChange, ariaLabel, children }) {
  return (
    <div className="flex flex-wrap items-end gap-x-1 px-3 pt-2 rounded-t-xl bg-gradient-to-b from-sky-100 to-sky-50 border border-gray-200">
      <div className="flex items-end gap-0.5" role="tablist" aria-label={ariaLabel}>
        {items.map((t, i) => {
          const active = value === t.key;
          const nextActive = items[i + 1]?.key === value;
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={active} disabled={t.disabled}
              title={t.disabled ? t.note : undefined} data-testid={t.testId}
              onClick={() => !t.disabled && onChange(t.key)}
              className={`group relative -mb-px flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-t-xl transition-colors ${active
                ? "z-10 bg-white text-indigo-700 border border-gray-200 border-b-white shadow-[0_-4px_10px_-6px_rgba(79,70,229,0.35)]"
                : t.disabled
                  ? "text-gray-300 cursor-not-allowed border border-transparent"
                  : "text-slate-600 border border-transparent hover:bg-sky-200/70 hover:text-slate-900"}`}>
              {active && <span aria-hidden="true" className="absolute inset-x-4 top-0 h-[3px] rounded-b-full bg-gradient-to-r from-indigo-500 to-violet-500" />}
              {active && <><Curve side="left" /><Curve side="right" /></>}
              {Icon && <Icon className={`h-4 w-4 ${active ? "text-indigo-600" : "text-sky-600/70 group-hover:text-sky-700"}`} />}
              {t.label}
              {t.badge > 0 && <span className="min-w-[1.25rem] px-1.5 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-bold text-center">{t.badge}</span>}
              {t.disabled && <span className="text-[10px] uppercase tracking-wide">soon</span>}
              {/* The thin divider between two inactive tabs, as Chrome draws it. */}
              {!active && !nextActive && i < items.length - 1 && (
                <span aria-hidden="true" className="absolute right-0 top-3 bottom-3 w-px bg-sky-300 group-hover:opacity-0" />
              )}
            </button>
          );
        })}
      </div>
      {children && <div className="ml-auto pb-2">{children}</div>}
    </div>
  );
}

export function SegmentedTabs({ items, value, onChange, ariaLabel, size = "sm" }) {
  const pad = size === "xs" ? "px-3 py-1 text-xs" : "px-4 py-1.5 text-sm";
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gradient-to-b from-violet-50 to-indigo-100 border border-indigo-200 shadow-inner w-fit"
      role="tablist" aria-label={ariaLabel}>
      {items.map((t) => {
        const active = value === t.key;
        const Icon = t.icon;
        return (
          <button key={t.key} type="button" role="tab" aria-selected={active} onClick={() => onChange(t.key)} data-testid={t.testId}
            className={`flex items-center gap-1.5 font-medium rounded-lg transition-all ${pad} ${active
              ? "bg-white text-indigo-700 shadow-md ring-1 ring-indigo-100"
              : "text-slate-600 hover:text-slate-900 hover:bg-white/70"}`}>
            {Icon && <Icon className={`h-4 w-4 ${active ? "text-indigo-600" : "text-indigo-400"}`} />}
            {t.label}
            {t.badge > 0 && (
              <span className={`min-w-[1.25rem] px-1.5 rounded-full text-[11px] font-bold text-center ${active ? "bg-indigo-600 text-white" : "bg-indigo-200 text-indigo-800"}`}>{t.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
